import { useState, useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import { TrendingUp } from "lucide-react"
import ToolShell from "../components/ToolShell"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import CTABox from "../components/CTABox"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"

export default function RentalYieldCalculatorPage() {
  const { tool, currency } = useOutletContext<{ tool: ToolDef, currency: string }>()

  const [propertyValue, setPropertyValue] = useState("")
  const [monthlyRent, setMonthlyRent] = useState("")
  const [annualCosts, setAnnualCosts] = useState("")
  const [mortgageInterest, setMortgageInterest] = useState("")

  const results = useMemo(() => {
    const pv = parseFloat(propertyValue)
    const mr = parseFloat(monthlyRent)
    const ac = parseFloat(annualCosts) || 0
    const mi = parseFloat(mortgageInterest) || 0
    if (!pv || !mr || pv === 0) return null
    const annualRent = mr * 12
    const grossYield = (annualRent / pv) * 100
    const netYield = ((annualRent - ac - mi) / pv) * 100
    const annualProfit = annualRent - ac - mi
    return { annualRent, grossYield, netYield, annualProfit }
  }, [propertyValue, monthlyRent, annualCosts, mortgageInterest])

  const getBenchmark = (y: number) => {
    if (y < 3) return { label: "Poor", cls: "text-red-600 bg-red-50 border-red-200" }
    if (y < 5) return { label: "Average", cls: "text-amber-600 bg-amber-50 border-amber-200" }
    if (y < 7) return { label: "Good", cls: "text-green-600 bg-green-50 border-green-200" }
    return { label: "Excellent", cls: "text-emerald-600 bg-emerald-50 border-emerald-200" }
  }

  const fmt = (n: number) => `${currency}${Math.round(n).toLocaleString()}`

  return (
    <div className="space-y-8">
      <ToolShell>
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Input Panel */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Property Details</p>

              {/* Global currency override injected automatically */}

              {([
                ["Property Value", propertyValue, setPropertyValue, "e.g. 250000"],
                ["Monthly Rent", monthlyRent, setMonthlyRent, "e.g. 1200"],
                ["Annual Running Costs", annualCosts, setAnnualCosts, "Optional — insurance, management"],
                ["Annual Mortgage Interest", mortgageInterest, setMortgageInterest, "Optional"],
              ] as const).map(([label, val, setter, ph]) => (
                <div key={label as string}>
                  <label className="block text-xs font-medium text-slate-500 mb-1">{label as string}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                    <input
                      type="number"
                      min="0"
                      value={val as string}
                      onChange={e => (setter as (v: string) => void)(e.target.value)}
                      placeholder={ph as string}
                      className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Result Panel */}
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Results</p>

              {!results ? (
                <p className="text-slate-400 text-sm text-center py-10">Enter property value and monthly rent to see results</p>
              ) : (
                <>
                  <div className="text-center py-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Gross Rental Yield</p>
                    <p className="text-5xl font-extrabold text-primary">{results.grossYield.toFixed(2)}%</p>
                    {(() => {
                      const b = getBenchmark(results.grossYield)
                      return (
                        <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold border ${b.cls}`}>
                          {b.label}
                        </span>
                      )
                    })()}
                  </div>

                  <div className="border-t border-primary/10 pt-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Net Yield</span>
                      <span className="font-semibold text-slate-900">{results.netYield.toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Annual Rental Income</span>
                      <span className="font-semibold text-slate-900">{fmt(results.annualRent)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Annual Net Profit</span>
                      <span className={`font-semibold ${results.annualProfit >= 0 ? "text-green-600" : "text-red-600"}`}>
                        {fmt(results.annualProfit)}
                      </span>
                    </div>
                  </div>

                  <p className="text-xs text-slate-500 border-t border-primary/10 pt-3">
                    Poor &lt;3% · Average 3–5% · Good 5–7% · Excellent &gt;7%
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </ToolShell>

      <FormulaBox
        formula={`Gross Yield (%) = (Monthly Rent × 12) ÷ Property Value × 100\nNet Yield (%) = (Annual Rent − Costs − Mortgage Interest) ÷ Property Value × 100`}
        description="Gross yield ignores costs. Net yield deducts your annual expenses and mortgage interest for a realistic return view."
      />

      <ToolFAQ
        items={[
          {
            q: "What is a good rental yield in the UK?",
            a: "A rental yield of 5–7% is generally considered good in the UK. Yields above 7% are excellent, typically seen in lower-value areas. In London and other high-value cities, 3–5% is more typical due to high purchase prices.",
          },
          {
            q: "What is the difference between gross and net rental yield?",
            a: "Gross yield is calculated from rental income alone, ignoring all expenses. Net yield deducts your annual running costs (management fees, maintenance, insurance) and mortgage interest, giving a much more accurate picture of actual returns.",
          },
          {
            q: "How can I improve my rental yield?",
            a: "You can improve yield by increasing rent to market rate, reducing void periods, cutting expenses through better management, or refinancing to lower mortgage interest. Buying in a high-rent area at a lower price is the single most impactful lever.",
          },
        ]}
      />

      <CTABox
        icon={TrendingUp}
        heading="Managing multiple properties?"
        body={tool.ctaText}
        ctaLabel="Start free"
        ctaHref="/register"
      />

      <RelatedToolsList relatedSlugs={tool.related} />
    </div>
  )
}
